import React, { useState, useMemo, useEffect } from 'react';
import { 
  Search, Filter, Plus, Clock, MapPin, User, Hash,
  CheckCircle2, AlertTriangle, Hourglass, MoreVertical,
  Square, CheckSquare, X, UserPlus, FolderInput, MessageSquare,
  SkipForward, Calendar, RotateCcw, Check, Edit3, Trash2, Loader2,
  Users, ChevronDown
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { zoneColors } from '../data/csvData';
import { getRoundColor, loadRoundColors, subscribeToRoundColors } from '../utils/roundColors';
import JobEditModal from '../components/JobEditModal';
import { subscribeToRefresh } from '../utils/dataSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getStatusConfig = (status) => {
  const configs = {
    scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-emerald-700', Icon: Clock },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700', Icon: CheckCircle2 },
    overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700', Icon: AlertTriangle },
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', Icon: Hourglass },
    done: { label: 'Done', color: 'bg-green-100 text-green-700', Icon: CheckCircle2 },
  };
  return configs[status] || configs.scheduled;
};

const JobCard = ({ job, index, isSelected, onToggleSelect, onClick, onEdit }) => {
  const [showMenu, setShowMenu] = useState(false);
  const statusConfig = getStatusConfig(job.status || 'scheduled');
  const StatusIcon = statusConfig.Icon;
  const customerName = job.customer_name || 'Unknown';
  const customerZone = job.customer_zone || job.zone || job.round || 'Unknown';
  const zoneInitials = customerZone.substring(0, 2).toUpperCase();
  
  const getDaysLabel = () => {
    const dateStr = job.scheduled_date || job.next_due;
    if (!dateStr) return '';
    const due = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    if (diff < 0) return `${Math.abs(diff)} Days Ago`;
    if (diff <= 7) return `In ${diff} Days`;
    if (diff <= 30) return `In ${Math.ceil(diff / 7)} Weeks`;
    return `In ${Math.ceil(diff / 30)} Months`;
  };
  
  return (
    <div 
      className={`bg-white border-b border-gray-100 p-4 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-pink-50' : ''}`}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start gap-3">
        <button 
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          className="mt-1 p-1 hover:bg-gray-100 rounded transition-colors"
          data-testid={`select-job-${job.id}`}
        >
          {isSelected ? (
            <CheckSquare size={20} className="text-pink-500" />
          ) : (
            <Square size={20} className="text-gray-300" />
          )}
        </button>

        <span className="mt-1 text-sm text-gray-400 w-6">{index}</span>

        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ backgroundColor: getRoundColor(customerZone) }}
        >
          {zoneInitials}
        </div>

        <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold text-gray-900">{customerName}</h3>
              <p className="text-sm text-gray-500 truncate">{job.customer_address?.split(',')[0] || job.address || ''}</p>
              <p className="text-sm text-gray-400">{job.services || job.service || 'Window Cleaning'}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-semibold text-gray-900">£{(job.price || 0).toFixed(2)}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {getDaysLabel() && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                getDaysLabel().includes('Ago') ? 'bg-red-100 text-red-600' : 
                getDaysLabel() === 'Today' ? 'bg-green-100 text-green-600' : 
                'bg-blue-100 text-emerald-600'
              }`}>
                {getDaysLabel()}
              </span>
            )}
            {job.assignee && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-600">
                {job.assignee}
              </span>
            )}
            <span 
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ 
                backgroundColor: `${getRoundColor(customerZone)}20`,
                color: getRoundColor(customerZone)
              }}
            >
              {customerZone}
            </span>
            {(job.status === 'done' || job.status === 'completed') && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-600 flex items-center gap-1">
                <Check size={10} />
                Done
              </span>
            )}
          </div>
        </div>

        <div className="relative">
          <button 
            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
          >
            <MoreVertical size={18} className="text-gray-400" />
          </button>
          
          {showMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-10 w-48 bg-white rounded-xl shadow-2xl z-50 overflow-hidden border border-zinc-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    if (onEdit) onEdit(job);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                >
                  <Edit3 size={16} className="text-zinc-400" />
                  <span className="text-sm">Edit Job</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left text-zinc-700 hover:bg-zinc-50 transition-colors"
                >
                  <User size={16} className="text-zinc-400" />
                  <span className="text-sm">View Customer</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 transition-colors border-t border-zinc-100"
                >
                  <Trash2 size={16} />
                  <span className="text-sm">Delete Job</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Re-assign Modal Component
const ReassignModal = ({ selectedCount, workers, onClose, onReassign, isLoading }) => {
  const [selectedWorker, setSelectedWorker] = useState('');
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Re-assign Jobs</h2>
            <p className="text-sm text-gray-500">{selectedCount} job{selectedCount !== 1 ? 's' : ''} selected</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Assign to worker</label>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            <button
              onClick={() => setSelectedWorker('')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                selectedWorker === '' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <X size={16} className="text-gray-500" />
              </div>
              <span className="text-gray-700">Unassigned</span>
              {selectedWorker === '' && <Check size={18} className="ml-auto text-emerald-600" />}
            </button>
            
            {workers.map(worker => (
              <button
                key={worker.name}
                onClick={() => setSelectedWorker(worker.name)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  selectedWorker === worker.name ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm font-medium">
                  {worker.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 text-left">
                  <span className="text-gray-900">{worker.name}</span>
                  <span className="text-xs text-gray-400 ml-2">({worker.job_count} jobs)</span>
                </div>
                {selectedWorker === worker.name && <Check size={18} className="text-emerald-600" />}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex gap-3 p-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button 
            onClick={() => onReassign(selectedWorker)}
            disabled={isLoading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            Re-assign
          </Button>
        </div>
      </div>
    </div>
  );
};

const JobList = ({ onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [jobs, setJobs] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingJob, setEditingJob] = useState(null);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);

  const [revenueBreakdown, setRevenueBreakdown] = useState({
    recurring_monthly: 0,
    recurring_annual: 0,
    recurring_count: 0,
    ad_hoc_total: 0,
    ad_hoc_count: 0,
    current_year: new Date().getFullYear()
  });

  const fetchRevenueSummary = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/reports/job-revenue-summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRevenueBreakdown(data);
      }
    } catch (error) {
      console.error('Error fetching revenue summary:', error);
    }
  };

  // Fetch ALL jobs (no pagination)
  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both array and {jobs: []} format
        const jobsList = Array.isArray(data) ? data : (data.jobs || []);
        setJobs(jobsList);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch unique assignees for filter badges
  const fetchAssignees = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/assignees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Backend returns a flat list: [{name, job_count}, ...]
        // (older callers expected {assignees: [...]} — keep both for safety)
        const list = Array.isArray(data) ? data : (data.assignees || []);
        setAssignees(list);
      }
    } catch (error) {
      console.error('Error fetching assignees:', error);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchAssignees();
    fetchRevenueSummary();
    loadRoundColors();
    
    const openCreate = sessionStorage.getItem('openCreateForm');
    if (openCreate === 'true') {
      setShowNewJobModal(true);
      sessionStorage.removeItem('openCreateForm');
    }
  }, []);

  // Re-render when round colors update so badges reflect the user's edits
  const [, setRoundColorTick] = useState(0);
  useEffect(() => {
    const unsub = subscribeToRoundColors(() => setRoundColorTick((t) => t + 1));
    return unsub;
  }, []);

  // "+ New → New Job" from the global header dispatches this event so the
  // modal opens even when we're already on the Jobs page.
  useEffect(() => {
    const handler = (e) => {
      const action = e?.detail?.action;
      if (action !== 'jobs' && action !== 'job-list') return;
      sessionStorage.removeItem('openCreateForm');
      setShowNewJobModal(true);
    };
    window.addEventListener('streakfree:open-create-form', handler);
    return () => window.removeEventListener('streakfree:open-create-form', handler);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToRefresh('joblist', (detail) => {
      console.log('[JobList] Refresh triggered by:', detail.source);
      fetchJobs();
      fetchAssignees();
    });
    return unsubscribe;
  }, []);

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const customerName = job.customer_name || '';
      const customerAddress = job.customer_address || job.address || '';
      const matchesSearch = 
        customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customerAddress.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      const matchesAssignee = assigneeFilter === 'all' || 
        (assigneeFilter === 'unassigned' ? !job.assignee : job.assignee === assigneeFilter);
      return matchesSearch && matchesStatus && matchesAssignee;
    });
  }, [jobs, searchQuery, statusFilter, assigneeFilter]);

  const totals = useMemo(() => {
    const totalPrice = filteredJobs.reduce((sum, job) => sum + (job.price || 0), 0);
    const totalDuration = filteredJobs.reduce((sum, job) => sum + (job.duration_minutes || 30), 0);
    const hours = Math.floor(totalDuration / 60);
    const mins = totalDuration % 60;
    return {
      count: filteredJobs.length,
      price: totalPrice,
      duration: `${hours}h ${mins}m`,
    };
  }, [filteredJobs]);

  const selectedTotals = useMemo(() => {
    const selected = filteredJobs.filter(job => selectedJobs.has(job.id));
    return {
      count: selected.length,
      price: selected.reduce((sum, job) => sum + (job.price || 0), 0),
    };
  }, [filteredJobs, selectedJobs]);

  const toggleJobSelection = (jobId) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const selectAllJobs = () => {
    if (selectedJobs.size === filteredJobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(filteredJobs.map(j => j.id)));
    }
  };

  const handleReassign = async (newAssignee) => {
    if (selectedJobs.size === 0) return;
    
    setIsReassigning(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/bulk-update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_ids: Array.from(selectedJobs),
          updates: { assignee: newAssignee }
        })
      });
      
      if (response.ok) {
        // Update local state
        setJobs(prev => prev.map(job => 
          selectedJobs.has(job.id) ? { ...job, assignee: newAssignee } : job
        ));
        setSelectedJobs(new Set());
        setShowReassignModal(false);
        // Refresh assignees
        fetchAssignees();
      } else {
        alert('Failed to re-assign jobs');
      }
    } catch (error) {
      console.error('Error reassigning jobs:', error);
      alert('Error reassigning jobs');
    } finally {
      setIsReassigning(false);
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
  };

  const handleJobSave = (updatedJob) => {
    setJobs(prev => prev.map(j => j.id === updatedJob.id ? updatedJob : j));
    fetchAssignees();
  };

  const statusOptions = ['all', 'scheduled', 'pending', 'overdue', 'completed'];

  return (
    <div className="h-full flex flex-col" data-testid="job-list-page">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">Job List</h1>
          <div className="flex items-center gap-3 text-sm">
            <span>{totals.count} jobs</span>
            <span>|</span>
            <span>£{totals.price.toFixed(2)}</span>
            <span>|</span>
            <span>{totals.duration}</span>
          </div>
        </div>
      </div>

      {/* Revenue Badges */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex gap-3">
          <div className="flex-1 bg-emerald-50 rounded-xl p-3 border border-emerald-100" data-testid="recurring-badge">
            <div className="flex items-center gap-2 mb-1">
              <RotateCcw size={14} className="text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">Recurring</span>
            </div>
            <p className="text-lg font-bold text-emerald-700">
              £{revenueBreakdown.recurring_monthly.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              <span className="text-xs font-normal text-emerald-500">/mo</span>
            </p>
            <p className="text-xs text-emerald-600">
              {revenueBreakdown.recurring_count} jobs • £{revenueBreakdown.recurring_annual.toLocaleString('en-GB', {minimumFractionDigits: 0, maximumFractionDigits: 0})}/yr
            </p>
          </div>
          
          <div className="flex-1 bg-amber-50 rounded-xl p-3 border border-amber-100" data-testid="adhoc-badge">
            <div className="flex items-center gap-2 mb-1">
              <Calendar size={14} className="text-amber-600" />
              <span className="text-xs font-medium text-amber-700">Ad Hoc ({revenueBreakdown.current_year})</span>
            </div>
            <p className="text-lg font-bold text-amber-700">
              £{revenueBreakdown.ad_hoc_total.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </p>
            <p className="text-xs text-amber-600">
              {revenueBreakdown.ad_hoc_count} jobs this year
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border-b border-gray-200 p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search jobs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        {/* Assignee Filter Badges */}
        {assignees.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Users size={14} />
              <span>Filter by worker:</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
              <button
                onClick={() => setAssigneeFilter('all')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                  assigneeFilter === 'all'
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
                data-testid="filter-all-assignees"
              >
                All Workers
              </button>
              <button
                onClick={() => setAssigneeFilter('unassigned')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  assigneeFilter === 'unassigned'
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="filter-unassigned"
              >
                Unassigned
              </button>
              {assignees.map(assignee => (
                <button
                  key={assignee.name}
                  onClick={() => setAssigneeFilter(assignee.name)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                    assigneeFilter === assignee.name
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  }`}
                  data-testid={`filter-${assignee.name}`}
                >
                  {assignee.name}
                  <span className={`text-xs ${assigneeFilter === assignee.name ? 'text-purple-200' : 'text-purple-400'}`}>
                    ({assignee.job_count})
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Status Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {statusOptions.map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === status
                  ? 'bg-zinc-900 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${status}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Select All Bar */}
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <button
          onClick={selectAllJobs}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          data-testid="select-all-btn"
        >
          {selectedJobs.size === filteredJobs.length && filteredJobs.length > 0 ? (
            <CheckSquare size={18} className="text-pink-500" />
          ) : (
            <Square size={18} className="text-gray-400" />
          )}
          <span>Select all ({filteredJobs.length})</span>
        </button>
        
        {selectedJobs.size > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{selectedJobs.size} selected</span>
            <Button
              onClick={() => setShowReassignModal(true)}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="reassign-btn"
            >
              <UserPlus size={14} className="mr-1" />
              Re-assign
            </Button>
          </div>
        )}
      </div>

      {/* Jobs List */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Calendar size={48} className="mb-4 text-gray-300" />
            <p>No jobs found</p>
          </div>
        ) : (
          filteredJobs.map((job, idx) => (
            <JobCard 
              key={job.id}
              job={job}
              index={idx + 1}
              isSelected={selectedJobs.has(job.id)}
              onToggleSelect={() => toggleJobSelection(job.id)}
              onClick={() => {}}
              onEdit={handleEditJob}
            />
          ))
        )}
      </div>

      {/* Selected Jobs Actions Bar */}
      {selectedTotals.count > 0 && (
        <div className="bg-pink-500 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{selectedTotals.count}</span>
            <span className="text-pink-100">selected</span>
            <span className="text-pink-200">•</span>
            <span className="font-semibold">£{selectedTotals.price.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setSelectedJobs(new Set())}
              className="px-3 py-1.5 bg-white/20 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors"
            >
              Clear
            </button>
            <button 
              onClick={() => setShowReassignModal(true)}
              className="px-3 py-1.5 bg-white text-pink-500 rounded-lg text-sm font-medium hover:bg-pink-50 transition-colors"
              data-testid="actions-button"
            >
              Re-assign
            </button>
          </div>
        </div>
      )}

      {/* FAB */}
      <button 
        onClick={() => setShowNewJobModal(true)}
        className="fixed bottom-20 left-6 w-14 h-14 bg-emerald-500 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 transition-colors z-30"
        data-testid="add-job-fab"
      >
        <Plus size={24} />
      </button>

      {/* Re-assign Modal */}
      {showReassignModal && (
        <ReassignModal
          selectedCount={selectedTotals.count}
          workers={assignees}
          onClose={() => setShowReassignModal(false)}
          onReassign={handleReassign}
          isLoading={isReassigning}
        />
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <JobEditModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={handleJobSave}
          onDelete={(job) => {
            setJobs(prev => prev.filter(j => j.id !== job.id));
            setEditingJob(null);
          }}
        />
      )}

      {/* New Job Modal */}
      {showNewJobModal && (
        <JobEditModal
          job={null}
          onClose={() => setShowNewJobModal(false)}
          onSave={(savedJob) => {
            fetchJobs();
            fetchAssignees();
            setShowNewJobModal(false);
          }}
        />
      )}
    </div>
  );
};

export default JobList;
